/* Aelse, 2026-10-16: the Instant welcome popup, steadied from the theme.
   Her words: "it kinda popped up in phases", "its like half the height for a moment", "i dont like the janky half
   height", "its also fucked on mobile". Record: brands/aelse/shopify-theme/2026-10-14-safari-add-to-bag/_note.md
   Instant draws the popup inside its own shadow root, which the theme's CSS cannot reach, so this adds one stylesheet
   inside it:
   1. The seal has a width but no height, and its file only starts loading when the popup opens, so the card opened
      ~190 px short and then jumped. While a popup image loads it now holds a square, transparent, then fades in; the
      hold comes off once the file is in, so a non-square image put in later still shows at its own shape.
   2. Phones: the 100 px side padding on the email box and button (set for desktop; Instant has no phone setting)
      squeezed them to ~150 px. On phones they get 45 px a side, the same as her text blocks; the box and button are
      44 px tall (a thumb's tap size; the button was 28 px) and the box's type is 16 px, because iPhone Safari zooms
      the whole page when a field under 16 px is tapped.
   3. Short phone screens get a 144 px seal so the card fits without scrolling. Phones under 373 px wide get 3 px
      heading letter spacing, so "ENTER SOMEWHERE" stays on one line.
   4. The close button: Instant draws it as a white disc, which glared on her dark popup (21:47). It is now a thin
      outline in the popup's own text colour (read from the heading), so it suits a light or a dark popup.
   If Instant changes its markup the rules stop matching and the popup is drawn exactly as Instant draws it. */
(() => {
  if (window.aePopup) return;
  window.aePopup = { version: 1 };

  const WAIT = 'data-ae-wait';
  const CSS = `
    form[role="dialog"] > button[aria-label="Close"] { background: transparent !important; color: var(--ae-ink, inherit) !important; border-color: var(--ae-ink, currentColor) !important; }
    form[role="dialog"] img { transition: opacity .35s ease; }
    form[role="dialog"] img[${WAIT}] { aspect-ratio: 1 / 1; opacity: 0; }
    @media (max-width: 600px) {
      form[role="dialog"] label:has(input),
      form[role="dialog"] div:has(> button[data-signup-form-action]) { padding-left: 45px !important; padding-right: 45px !important; }
      form[role="dialog"] input { font-size: 16px !important; height: 44px !important; }
      form[role="dialog"] button[data-signup-form-action] { height: 44px !important; }
    }
    @media (max-width: 600px) and (max-height: 760px) {
      form[role="dialog"] img { width: 144px !important; }
    }
    @media (max-width: 372px) {
      form[role="dialog"] h1 { letter-spacing: 3px !important; }
    }
    @media (prefers-reduced-motion: reduce) {
      form[role="dialog"] img { transition: none; }
    }`;

  let sheet = null;
  try {
    sheet = new CSSStyleSheet();
    sheet.replaceSync(CSS);
  } catch (error) {
    sheet = null;
  }

  function addStyle(root) {
    if (sheet && 'adoptedStyleSheets' in root) {
      if (!root.adoptedStyleSheets.includes(sheet)) root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
    } else if (!root.querySelector('style[data-ae-popup]')) {
      const tag = document.createElement('style');
      tag.setAttribute('data-ae-popup', '');
      tag.textContent = CSS;
      root.appendChild(tag);
    }
  }

  // Hold the image's square until its file is in (or has failed), then let it fade in.
  function hold(img) {
    const src = img.getAttribute('src') || '';
    if (img.aeSrc === src) return;
    img.aeSrc = src;
    if (!src || img.complete) {
      img.removeAttribute(WAIT);
      return;
    }
    img.setAttribute(WAIT, '');
    const done = () => {
      if (img.aeSrc === src) img.removeAttribute(WAIT);
    };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
    setTimeout(done, 5000);
  }

  // Mutation observers run before the next paint, so a new image is held before it is ever drawn at zero height.
  function watch(host) {
    const root = host.shadowRoot;
    if (!root) return false;
    if (root.aeWatched) return true;
    root.aeWatched = true;
    const scan = () => {
      addStyle(root);
      root.querySelectorAll('img').forEach(hold);
      // The close button follows the popup's own text colour, light popup or dark.
      const form = root.querySelector('form[role="dialog"]');
      const ink = form && form.querySelector('h1, p');
      if (ink) {
        const color = getComputedStyle(ink).color;
        if (form.style.getPropertyValue('--ae-ink') !== color) form.style.setProperty('--ae-ink', color);
      }
    };
    new MutationObserver(scan).observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['src'] });
    scan();
    return true;
  }

  const isHost = (node) => node.nodeType === 1 && /^ic-signup-form-/.test(node.id || '') && node.tagName !== 'STYLE';

  function tryHost(host, tries = 0) {
    if (watch(host) || tries > 60) return;
    setTimeout(() => tryHost(host, tries + 1), 50);
  }

  function start() {
    Array.from(document.body.children).filter(isHost).forEach((host) => tryHost(host));
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) if (isHost(node)) tryHost(node);
      }
    }).observe(document.body, { childList: true });
  }

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
})();
